// =============================================================================
// Admin Orders Page — Cross-organization order management
// =============================================================================

import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router';
import { Card, Input, Select, Badge, Spinner, Button, TagOverflowList } from '@tkpod/ui';
import { useApiClient } from '@tkpod/api-client';
import {
  ORDER_STATUS_LABELS,
  ORDER_STATUS_MERCHANT_VARIANT,
  getMerchantOrderStatusLabel,
  isMerchantMaskedDanger,
  hasPermission,
} from '@tkpod/shared';
import { OrderStatsBar, type StatKey } from '../components/order/OrderStatsBar';
import { SettlementBadge } from '../components/order/SettlementBadge';
import { OrganizationPicker } from '../components/OrganizationPicker';
import { SendToProviderModal, type InkteoFulfillmentChoice, type ProductionTypeChoice } from '../components/order/SendToProviderModal';
import { useAuth } from '@/hooks/use-auth';

const ORDER_STATUSES = [
  { value: '', label: 'All Statuses' },
  { value: 'DRAFT', label: 'Draft' },
  { value: 'NEW', label: 'New' },
  { value: 'AWAITING_FULFILLMENT_REQUEST', label: 'Awaiting Approval' },
  { value: 'DESIGN_PENDING', label: 'Design Pending' },
  { value: 'EXPORT_FAILED', label: 'Export Failed' },
  { value: 'IN_PRODUCTION', label: 'In Production' },
  { value: 'PRODUCTION_DONE', label: 'Produced' },
  { value: 'READY_TO_SHIP', label: 'Ready to Ship' },
  { value: 'SHIPPED', label: 'Shipped' },
  { value: 'DELIVERED', label: 'Delivered' },
  { value: 'CANCELLED', label: 'Cancelled' },
  { value: 'ON_HOLD', label: 'On Hold' },
  { value: 'CANCEL_REQUESTED', label: 'Cancel Requested' },
  { value: 'FAILED', label: 'Failed' },
];

/**
 * Wholesale settlement quick-filter options. `PAYMENT_PENDING` is a composite
 * (PENDING + CHARGING + ON_HOLD) mirrored server-side — it backs the stats-bar
 * "Payment pending" quick filter.
 */
const SETTLEMENT_OPTIONS = [
  { value: '', label: 'All Settlements' },
  { value: 'PAYMENT_PENDING', label: 'Payment Pending (any)' },
  { value: 'PENDING', label: 'Pending' },
  { value: 'CHARGING', label: 'Charging' },
  { value: 'ON_HOLD', label: 'On Hold' },
  { value: 'PAID', label: 'Paid' },
  { value: 'FAILED', label: 'Failed' },
  { value: 'NONE', label: 'None (self-fulfilled)' },
];

const SELF_CLAIM_OPTIONS = [
  { value: '', label: 'All self-fulfill' },
  { value: 'UNCLAIMED', label: 'Unclaimed SELF' },
  { value: 'CLAIMED', label: 'Claimed SELF' },
];

const SORT_OPTIONS = [
  { value: 'createdAt:desc', label: 'Newest First' },
  { value: 'createdAt:asc', label: 'Oldest First' },
  { value: 'total:desc', label: 'Highest Total' },
  { value: 'total:asc', label: 'Lowest Total' },
  { value: 'orderNumber:desc', label: 'Order # (Z→A)' },
  { value: 'orderNumber:asc', label: 'Order # (A→Z)' },
];

const BULK_BLOCKED = new Set([
  'CANCELLED',
  'FAILED',
  'ON_HOLD',
  'FRAUD_HOLD',
  'CANCEL_REQUESTED',
]);

interface Order {
  id: string;
  orderNumber: string;
  customerName: string | null;
  customerEmail: string | null;
  organizationId: string;
  organizationName: string;
  /** Originating store (connection) — null for manually created orders. */
  store: { id: string; name: string; shopDomain: string | null } | null;
  status: string;
  fulfillmentStatus: string;
  source: string;
  providerName: string | null;
  itemCount: number;
  total: string;
  currency: string;
  // Wholesale settlement (what TK collects from the merchant) — distinct from
  // the Shopify customer paymentStatus. Drives the Settlement badge column.
  wholesaleChargeStatus: string;
  // Order-level fulfillment shape derived from line snapshots.
  fulfillmentMix: 'ALL_TK_POD' | 'ALL_SELF' | 'MIXED';
  fulfillmentRequestedAt: string | null;
  selfClaim: {
    status: 'UNCLAIMED' | 'CLAIMED';
    providerName: string | null;
    fulfillmentType: string | null;
    unclaimedSelfCount: number;
    canSend?: boolean;
    suggestedFulfillmentType?: 'SHIPPING' | 'PICKUP';
  } | null;
  /** At least one external provider submission FAILED — needs admin attention. */
  hasProviderFailure: boolean;
  /** Merchant/store tags (Shopify-synced) — read-only in the admin. */
  tags: string[];
  productionType?: 'REGULAR' | 'RUSH';
  /** Admin-only operational tags — never synced to Shopify or the portal. */
  internalTags: string[];
  latestNote: string | null;
  createdAt: string;
}

function isSendEligible(o: Order): boolean {
  if (BULK_BLOCKED.has(o.status)) return false;
  if (o.fulfillmentMix !== 'ALL_SELF' && o.fulfillmentMix !== 'MIXED') return false;
  if (o.selfClaim?.canSend === false) return false;
  return o.selfClaim?.status === 'UNCLAIMED';
}

interface OrdersResponse {
  data: Order[];
  pagination: { page: number; limit: number; total: number; totalPages: number };
}

interface ProviderOption {
  id: string;
  name: string;
}

interface UptOption {
  id: string;
  name: string;
}

function FilterIcon({ className }: { className?: string }) {
  return (
    <svg className={className} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
    </svg>
  );
}

function NoteIcon({ className }: { className?: string }) {
  return (
    <svg className={className} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" /><line x1="10" y1="9" x2="8" y2="9" />
    </svg>
  );
}

function XIcon({ className }: { className?: string }) {
  return (
    <svg className={className} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

export function AdminOrdersPage() {
  const api = useApiClient();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canSendToProvider = hasPermission(
    user?.permissions ?? user?.currentOrganization?.permissions ?? [],
    'order:update',
  );
  const [searchParams, setSearchParams] = useSearchParams();
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [backfillFeedback, setBackfillFeedback] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkModalOpen, setBulkModalOpen] = useState(false);
  const [bulkFulfillmentType, setBulkFulfillmentType] =
    useState<InkteoFulfillmentChoice>('SHIPPING');
  const [bulkProductionType, setBulkProductionType] =
    useState<ProductionTypeChoice>('DETECTED');
  const [bulkError, setBulkError] = useState<string | null>(null);

  // ── Read URL params ──
  const page = Number(searchParams.get('page') ?? '1');
  const status = searchParams.get('status') ?? '';
  const search = searchParams.get('search') ?? '';
  const orderNumber = searchParams.get('orderNumber') ?? '';
  const externalOrderId = searchParams.get('externalOrderId') ?? '';
  const wholesaleChargeStatus = searchParams.get('wholesaleChargeStatus') ?? '';
  const providerId = searchParams.get('providerId') ?? '';
  const organizationId = searchParams.get('organizationId') ?? '';
  const source = searchParams.get('source') ?? '';
  const dateFrom = searchParams.get('dateFrom') ?? '';
  const dateTo = searchParams.get('dateTo') ?? '';
  const uptId = searchParams.get('uptId') ?? '';
  const routingStatus = searchParams.get('routingStatus') ?? '';
  const fulfillmentRequested = searchParams.get('fulfillmentRequested') ?? '';
  const providerFailed = searchParams.get('providerFailed') ?? '';
  const selfClaimStatus = searchParams.get('selfClaimStatus') ?? '';
  const totalMin = searchParams.get('totalMin') ?? '';
  const totalMax = searchParams.get('totalMax') ?? '';
  const tag = searchParams.get('tag') ?? '';
  const sort = searchParams.get('sort') ?? 'createdAt:desc';

  const [sortBy, sortOrder] = sort.split(':') as [string, string];

  const [searchInput, setSearchInput] = useState(search);
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (status) count++;
    if (orderNumber) count++;
    if (externalOrderId) count++;
    if (wholesaleChargeStatus) count++;
    if (providerId) count++;
    if (organizationId) count++;
    if (source) count++;
    if (uptId) count++;
    if (routingStatus) count++;
    if (fulfillmentRequested) count++;
    if (providerFailed) count++;
    if (selfClaimStatus) count++;
    if (dateFrom) count++;
    if (dateTo) count++;
    if (totalMin) count++;
    if (totalMax) count++;
    if (tag) count++;
    return count;
  }, [status, orderNumber, externalOrderId, wholesaleChargeStatus, providerId, organizationId, source, uptId, routingStatus, fulfillmentRequested, providerFailed, selfClaimStatus, dateFrom, dateTo, totalMin, totalMax, tag]);

  const setFilter = useCallback(
    (key: string, value: string) => {
      const next = new URLSearchParams(searchParams);
      if (value) {
        next.set(key, value);
      } else {
        next.delete(key);
      }
      if (key !== 'page') next.set('page', '1');
      setSearchParams(next);
    },
    [searchParams, setSearchParams],
  );

  const clearAllFilters = useCallback(() => {
    const next = new URLSearchParams();
    next.set('page', '1');
    setSearchParams(next);
    setSearchInput('');
  }, [setSearchParams]);

  const setFilterRef = useRef(setFilter);
  setFilterRef.current = setFilter;

  useEffect(() => {
    setSearchInput(search);
  }, [search]);

  useEffect(() => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setFilterRef.current('search', searchInput);
      searchDebounceRef.current = null;
    }, 400);
    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
  }, [searchInput]);

  // ── Data queries ──
  const statsQuery = useQuery({
    queryKey: ['admin', 'orders', 'stats'],
    queryFn: () => api.get<{ data: Record<string, number> }>('/api/v1/admin/orders/stats'),
  });

  const providersQuery = useQuery({
    queryKey: ['admin', 'providers', 'list'],
    queryFn: () => api.get<{ data: ProviderOption[] }>('/api/v1/admin/providers?limit=100'),
    staleTime: 60_000,
  });

  const uptQuery = useQuery({
    queryKey: ['admin', 'catalog', 'templates', 'list'],
    queryFn: () => api.get<{ data: UptOption[] }>('/api/v1/admin/catalog/templates?limit=100'),
    staleTime: 60_000,
  });

  const ordersQuery = useQuery({
    queryKey: ['admin', 'orders', { page, status, search, orderNumber, externalOrderId, wholesaleChargeStatus, providerId, organizationId, source, uptId, routingStatus, fulfillmentRequested, providerFailed, selfClaimStatus, dateFrom, dateTo, totalMin, totalMax, tag, sortBy, sortOrder }],
    queryFn: () => {
      const params = new URLSearchParams();
      params.set('page', String(page));
      params.set('limit', '20');
      if (status) params.set('status', status);
      if (search) params.set('search', search);
      if (orderNumber) params.set('orderNumber', orderNumber);
      if (externalOrderId) params.set('externalOrderId', externalOrderId);
      if (wholesaleChargeStatus) params.set('wholesaleChargeStatus', wholesaleChargeStatus);
      if (providerId) params.set('providerId', providerId);
      if (organizationId) params.set('organizationId', organizationId);
      if (source) params.set('source', source);
      if (uptId) params.set('uptId', uptId);
      if (routingStatus) params.set('routingStatus', routingStatus);
      if (fulfillmentRequested) params.set('fulfillmentRequested', fulfillmentRequested);
      if (providerFailed) params.set('providerFailed', providerFailed);
      if (selfClaimStatus) params.set('selfClaimStatus', selfClaimStatus);
      if (dateFrom) params.set('dateFrom', dateFrom);
      if (dateTo) params.set('dateTo', dateTo);
      if (totalMin) params.set('totalMin', totalMin);
      if (totalMax) params.set('totalMax', totalMax);
      if (tag) params.set('tag', tag);
      if (sortBy) params.set('sortBy', sortBy);
      if (sortOrder) params.set('sortOrder', sortOrder);
      return api.get<OrdersResponse>(`/api/v1/admin/orders?${params.toString()}`);
    },
    refetchInterval: 25_000,
  });

  const applySearchNow = useCallback(() => {
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
      searchDebounceRef.current = null;
    }
    setFilter('search', searchInput);
  }, [searchInput, setFilter]);

  const providerOptions: { value: string; label: string }[] = useMemo(() => {
    const items = providersQuery.data?.data ?? [];
    return [
      { value: '', label: 'All Providers' },
      ...items.map((p) => ({ value: p.id, label: p.name })),
    ];
  }, [providersQuery.data]);

  const uptOptions: { value: string; label: string }[] = useMemo(() => {
    const items = uptQuery.data?.data ?? [];
    return [
      { value: '', label: 'All Templates' },
      ...items.map((u) => ({ value: u.id, label: u.name })),
    ];
  }, [uptQuery.data]);

  const sourceOptions = [
    { value: '', label: 'All Sources' },
    { value: 'MANUAL', label: 'Manual' },
    { value: 'SHOPIFY', label: 'Shopify' },
    { value: 'WOOCOMMERCE', label: 'WooCommerce' },
    { value: 'API', label: 'API' },
  ];

  const orders = ordersQuery.data?.data ?? [];
  const pagination = ordersQuery.data?.pagination;

  const pageEligibleIds = useMemo(
    () => orders.filter(isSendEligible).map((o) => o.id),
    [orders],
  );
  const allPageSelected =
    pageEligibleIds.length > 0 &&
    pageEligibleIds.every((id) => selectedIds.has(id));
  const selectedOrders = orders.filter((o) => selectedIds.has(o.id));
  const selectedEligible = selectedOrders.filter(isSendEligible);
  const selectedSkipCount = selectedOrders.length - selectedEligible.length;
  const selectedSelfLineCount = selectedEligible.reduce(
    (sum, o) => sum + (o.selfClaim?.unclaimedSelfCount ?? 0),
    0,
  );
  const allPickupSuggested =
    selectedEligible.length > 0 &&
    selectedEligible.every(
      (o) => o.selfClaim?.suggestedFulfillmentType === 'PICKUP',
    );
  const bulkMixedCheckout =
    selectedEligible.some(
      (o) => o.selfClaim?.suggestedFulfillmentType === 'PICKUP',
    ) &&
    selectedEligible.some(
      (o) => o.selfClaim?.suggestedFulfillmentType !== 'PICKUP',
    );

  useEffect(() => {
    if (allPickupSuggested) {
      setBulkFulfillmentType('PICKUP');
    }
  }, [allPickupSuggested]);
  const selectedStoreLabel =
    selectedEligible.length === 1
      ? selectedEligible[0]!.store?.name ?? 'Unknown store'
      : `${selectedEligible.length} orders`;

  const toggleSelected = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAllPage = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allPageSelected) {
        for (const id of pageEligibleIds) next.delete(id);
      } else {
        for (const id of pageEligibleIds) next.add(id);
      }
      return next;
    });
  };

  const bulkSendMutation = useMutation({
    mutationFn: () =>
      api.post<{
        data: { results: Array<{ orderId: string; ok: boolean; error?: string }> };
      }>('/api/v1/admin/orders/send-to-provider', {
        orderIds: selectedEligible.map((o) => o.id),
        fulfillmentType: bulkFulfillmentType,
        ...(bulkProductionType === 'DETECTED'
          ? {}
          : { productionType: bulkProductionType }),
      }),
    onSuccess: (res) => {
      const failed = res.data.results.filter((r) => !r.ok);
      setSelectedIds(new Set());
      setBulkModalOpen(false);
      setBulkError(null);
      void queryClient.invalidateQueries({ queryKey: ['admin', 'orders'] });
      if (failed.length > 0) {
        setBackfillFeedback(
          `Sent with ${failed.length} skip(s): ${failed.map((f) => f.error).join('; ')}`,
        );
      }
    },
    onError: (err: Error) => {
      setBulkError(err.message);
    },
  });

  /**
   * Stats-bar quick filters. Each item maps to a fresh, single-filter view
   * so the counts shown in the bar match the resulting list exactly.
   */
  const handleStatSelect = useCallback(
    (key: StatKey) => {
      const next = new URLSearchParams();
      next.set('page', '1');
      if (key === 'TODAY') {
        // Stats use the server-UTC day boundary; mirror it here.
        next.set('dateFrom', new Date().toISOString().slice(0, 10));
      } else if (key === 'PAYMENT_PENDING') {
        next.set('wholesaleChargeStatus', 'PAYMENT_PENDING');
      } else if (key === 'UNROUTED') {
        next.set('routingStatus', 'UNROUTED');
      }
      // TOTAL falls through with no filters — clears everything.
      setSearchParams(next);
      setSearchInput('');
      if (key !== 'TOTAL') setFiltersOpen(true);
    },
    [setSearchParams],
  );

  /**
   * One-click recovery for Shopify orders that were ingested BEFORE the
   * routing pipeline was wired into the webhook. Walks every SHOPIFY order
   * with no resolved provider and re-runs autoRoute. Idempotent — scoped
   * server-side to orders with `resolvedProviderId = null`.
   */
  const backfillShopifyRoutingMutation = useMutation({
    mutationFn: () =>
      api.post<{
        total: number;
        routed: number;
        unrouted: number;
        failed: number;
      }>('/api/v1/admin/orders/backfill-shopify-routing'),
    onSuccess: (res) => {
      setBackfillFeedback(
        `Backfill complete — total: ${res.total}, routed: ${res.routed}, unrouted: ${res.unrouted}, failed: ${res.failed}`,
      );
      queryClient.invalidateQueries({ queryKey: ['admin', 'orders'] });
    },
    onError: (err: Error) =>
      setBackfillFeedback(
        err.message || 'Backfill failed. Check server logs.',
      ),
  });

  return (
    <div className="space-y-6">
      <OrderStatsBar
        stats={statsQuery.data?.data}
        isLoading={statsQuery.isLoading}
        onSelect={handleStatSelect}
      />

      <Card padding="none">
        {/* Header */}
        <div className="flex flex-col gap-4 border-b border-border px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-semibold text-text-primary">All Orders</h2>
              {source === 'SHOPIFY' && (
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={backfillShopifyRoutingMutation.isPending}
                  onClick={() => {
                    setBackfillFeedback(null);
                    backfillShopifyRoutingMutation.mutate();
                  }}
                  title="Re-run provider routing on every Shopify order that has no resolved provider. Idempotent and safe."
                >
                  {backfillShopifyRoutingMutation.isPending
                    ? 'Backfilling…'
                    : 'Backfill Shopify routing'}
                </Button>
              )}
            </div>
            {pagination && (
              <p className="mt-0.5 text-xs text-text-tertiary">
                {pagination.total} order{pagination.total !== 1 ? 's' : ''}
              </p>
            )}
            {backfillFeedback && (
              <p className="mt-1 text-xs text-text-secondary">
                {backfillFeedback}
              </p>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="min-w-[200px] flex-1 sm:max-w-xs">
              <Input
                placeholder="Order #, Shopify order #, customer, store..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && applySearchNow()}
                aria-label="Search orders"
              />
            </div>
            <button
              onClick={() => setFiltersOpen((v) => !v)}
              className={`inline-flex items-center gap-1.5 rounded-md border px-3 py-2 text-xs font-medium transition-colors ${
                filtersOpen || activeFilterCount > 0
                  ? 'border-brand-500 bg-brand-50 text-brand-700'
                  : 'border-border bg-white text-text-secondary hover:bg-surface-secondary hover:text-text-primary'
              }`}
            >
              <FilterIcon className="shrink-0" />
              Filters
              {activeFilterCount > 0 && (
                <span className="flex h-5 min-w-[20px] items-center justify-center rounded-full bg-brand-500 px-1.5 text-[10px] font-semibold text-white">
                  {activeFilterCount}
                </span>
              )}
            </button>
            <div className="w-[160px]">
              <Select
                value={sort}
                onChange={(e) => setFilter('sort', e.target.value)}
                options={SORT_OPTIONS}
                aria-label="Sort orders"
              />
            </div>
          </div>
        </div>

        {/* Expanded filter panel */}
        {filtersOpen && (
          <div className="border-b border-border bg-surface-secondary/30 px-5 py-4">
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6">
              <Select
                label="Status"
                value={status}
                onChange={(e) => setFilter('status', e.target.value)}
                options={ORDER_STATUSES}
              />
              <DebouncedFilterInput
                label="App Order #"
                placeholder="TK-00042"
                value={orderNumber}
                onCommit={(v) => setFilter('orderNumber', v)}
              />
              <DebouncedFilterInput
                label="Shopify Order #"
                placeholder="#1010 or 549..."
                value={externalOrderId}
                onCommit={(v) => setFilter('externalOrderId', v)}
              />
              <Select
                label="Settlement"
                value={wholesaleChargeStatus}
                onChange={(e) => setFilter('wholesaleChargeStatus', e.target.value)}
                options={SETTLEMENT_OPTIONS}
              />
              <Select
                label="Provider"
                value={providerId}
                onChange={(e) => setFilter('providerId', e.target.value)}
                options={providerOptions}
              />
              <OrganizationPicker
                label="Organization"
                value={organizationId}
                onChange={(id) => setFilter('organizationId', id)}
              />
              <Select
                label="Source"
                value={source}
                onChange={(e) => setFilter('source', e.target.value)}
                options={sourceOptions}
              />
              <Select
                label="Template (UPT)"
                value={uptId}
                onChange={(e) => setFilter('uptId', e.target.value)}
                options={uptOptions}
              />
              <Select
                label="Routing"
                value={routingStatus}
                onChange={(e) => setFilter('routingStatus', e.target.value)}
                options={[
                  { value: '', label: 'All' },
                  { value: 'ROUTED', label: 'Routed' },
                  { value: 'UNROUTED', label: 'Unrouted' },
                ]}
              />
              <Select
                label="Fulfillment Requested"
                value={fulfillmentRequested}
                onChange={(e) => setFilter('fulfillmentRequested', e.target.value)}
                options={[
                  { value: '', label: 'All' },
                  { value: 'true', label: 'Requested' },
                  { value: 'false', label: 'Not Requested' },
                ]}
              />
              <Select
                label="Provider Issues"
                value={providerFailed}
                onChange={(e) => setFilter('providerFailed', e.target.value)}
                options={[
                  { value: '', label: 'All' },
                  { value: 'true', label: 'Provider failed' },
                ]}
              />
              <Select
                label="Self-fulfill claim"
                value={selfClaimStatus}
                onChange={(e) => setFilter('selfClaimStatus', e.target.value)}
                options={SELF_CLAIM_OPTIONS}
              />
              <Input
                label="Date From"
                type="date"
                lang="en"
                value={dateFrom}
                onChange={(e) => setFilter('dateFrom', e.target.value)}
              />
              <Input
                label="Date To"
                type="date"
                lang="en"
                value={dateTo}
                onChange={(e) => setFilter('dateTo', e.target.value)}
              />
              <Input
                label="Min Total ($)"
                type="number"
                min={0}
                step="0.01"
                placeholder="0.00"
                value={totalMin}
                onChange={(e) => setFilter('totalMin', e.target.value)}
              />
              <Input
                label="Max Total ($)"
                type="number"
                min={0}
                step="0.01"
                placeholder="999.99"
                value={totalMax}
                onChange={(e) => setFilter('totalMax', e.target.value)}
              />
              <DebouncedFilterInput
                label="Tag"
                placeholder="Internal or store tag"
                value={tag}
                onCommit={(v) => setFilter('tag', v)}
              />
            </div>

            {activeFilterCount > 0 && (
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <span className="text-xs text-text-tertiary">Active:</span>
                {status && (
                  <FilterChip label={`Status: ${ORDER_STATUSES.find((s) => s.value === status)?.label ?? status}`} onRemove={() => setFilter('status', '')} />
                )}
                {orderNumber && (
                  <FilterChip label={`Order #: ${orderNumber}`} onRemove={() => setFilter('orderNumber', '')} />
                )}
                {externalOrderId && (
                  <FilterChip label={`Shopify #: ${externalOrderId}`} onRemove={() => setFilter('externalOrderId', '')} />
                )}
                {wholesaleChargeStatus && (
                  <FilterChip label={`Settlement: ${SETTLEMENT_OPTIONS.find((s) => s.value === wholesaleChargeStatus)?.label ?? wholesaleChargeStatus}`} onRemove={() => setFilter('wholesaleChargeStatus', '')} />
                )}
                {providerId && (
                  <FilterChip label={`Provider: ${providerOptions.find((p) => p.value === providerId)?.label ?? providerId}`} onRemove={() => setFilter('providerId', '')} />
                )}
                {organizationId && (
                  <FilterChip label="Organization filter" onRemove={() => setFilter('organizationId', '')} />
                )}
                {source && (
                  <FilterChip label={`Source: ${source}`} onRemove={() => setFilter('source', '')} />
                )}
                {uptId && (
                  <FilterChip label={`UPT: ${uptOptions.find((u) => u.value === uptId)?.label ?? uptId}`} onRemove={() => setFilter('uptId', '')} />
                )}
                {routingStatus && (
                  <FilterChip label={`Routing: ${routingStatus}`} onRemove={() => setFilter('routingStatus', '')} />
                )}
                {fulfillmentRequested && (
                  <FilterChip
                    label={`Fulfillment: ${fulfillmentRequested === 'true' ? 'Requested' : 'Not requested'}`}
                    onRemove={() => setFilter('fulfillmentRequested', '')}
                  />
                )}
                {providerFailed && (
                  <FilterChip
                    label="Provider failed"
                    onRemove={() => setFilter('providerFailed', '')}
                  />
                )}
                {selfClaimStatus && (
                  <FilterChip
                    label={`SELF: ${SELF_CLAIM_OPTIONS.find((s) => s.value === selfClaimStatus)?.label ?? selfClaimStatus}`}
                    onRemove={() => setFilter('selfClaimStatus', '')}
                  />
                )}
                {dateFrom && (
                  <FilterChip label={`From: ${dateFrom}`} onRemove={() => setFilter('dateFrom', '')} />
                )}
                {dateTo && (
                  <FilterChip label={`To: ${dateTo}`} onRemove={() => setFilter('dateTo', '')} />
                )}
                {totalMin && (
                  <FilterChip label={`Min: $${totalMin}`} onRemove={() => setFilter('totalMin', '')} />
                )}
                {totalMax && (
                  <FilterChip label={`Max: $${totalMax}`} onRemove={() => setFilter('totalMax', '')} />
                )}
                {tag && (
                  <FilterChip label={`Tag: ${tag}`} onRemove={() => setFilter('tag', '')} />
                )}

                <button
                  onClick={clearAllFilters}
                  className="text-xs font-medium text-brand-600 hover:underline"
                >
                  Clear all
                </button>
              </div>
            )}
          </div>
        )}

        {/* Table */}
        {ordersQuery.isLoading ? (
          <div className="flex items-center justify-center py-20">
            <Spinner size="lg" />
          </div>
        ) : ordersQuery.isError ? (
          <div className="flex flex-col items-center justify-center gap-3 py-20">
            <p className="text-sm font-medium text-red-600">Failed to load orders.</p>
            <button
              onClick={() => ordersQuery.refetch()}
              className="rounded-md border border-border bg-white px-3 py-1.5 text-xs font-medium text-text-primary transition-colors hover:bg-surface-secondary"
            >
              Retry
            </button>
          </div>
        ) : orders.length === 0 ? (
          <div className="py-20 text-center">
            <p className="text-sm text-text-secondary">No orders found.</p>
            <p className="mt-1 text-xs text-text-tertiary">Try adjusting your search or filters.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            {canSendToProvider && selectedIds.size > 0 && (
              <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border bg-surface-secondary/40 px-5 py-2">
                <p className="text-xs text-text-secondary">
                  {selectedIds.size} selected
                  {selectedSkipCount > 0
                    ? ` · ${selectedSkipCount} ineligible`
                    : ''}
                </p>
                <Button
                  variant="primary"
                  size="sm"
                  disabled={selectedEligible.length === 0}
                  onClick={() => {
                    setBulkFulfillmentType('SHIPPING');
                    setBulkProductionType('DETECTED');
                    setBulkError(null);
                    setBulkModalOpen(true);
                  }}
                >
                  Send to provider
                </Button>
              </div>
            )}
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-surface-secondary/40">
                  {canSendToProvider && (
                    <th className="w-10 px-3 py-2.5">
                      <input
                        type="checkbox"
                        aria-label="Select all eligible on this page"
                        checked={allPageSelected}
                        disabled={pageEligibleIds.length === 0}
                        onChange={toggleSelectAllPage}
                      />
                    </th>
                  )}
                  <th className="px-5 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-text-tertiary">
                    Order
                  </th>
                  <th className="px-5 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-text-tertiary">
                    Customer
                  </th>
                  <th className="hidden px-5 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-text-tertiary md:table-cell">
                    Organization
                  </th>
                  <th className="hidden px-5 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-text-tertiary lg:table-cell">
                    Store
                  </th>
                  <th className="px-5 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-text-tertiary">
                    Status
                  </th>
                  <th className="hidden px-5 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-text-tertiary sm:table-cell">
                    Tags
                  </th>
                  <th className="hidden px-5 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-text-tertiary md:table-cell">
                    Settlement
                  </th>
                  <th className="hidden px-5 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-text-tertiary lg:table-cell">
                    Provider
                  </th>
                  <th className="hidden px-5 py-2.5 text-right text-[11px] font-semibold uppercase tracking-wider text-text-tertiary sm:table-cell">
                    Items
                  </th>
                  <th className="px-5 py-2.5 text-right text-[11px] font-semibold uppercase tracking-wider text-text-tertiary">
                    Total
                  </th>
                  <th className="hidden px-5 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-text-tertiary lg:table-cell">
                    Date
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {orders.map((o) => (
                  <tr
                    key={o.id}
                    className="cursor-pointer transition-colors hover:bg-surface-secondary/60"
                    onClick={() => navigate(`/orders/${o.id}`)}
                  >
                    {canSendToProvider && (
                      <td
                        className="px-3 py-2.5"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <input
                          type="checkbox"
                          aria-label={`Select ${o.orderNumber}`}
                          checked={selectedIds.has(o.id)}
                          disabled={!isSendEligible(o)}
                          onChange={() => toggleSelected(o.id)}
                        />
                      </td>
                    )}
                    <td className="px-5 py-2.5 font-medium text-brand-600 whitespace-nowrap">
                      <span className="inline-flex items-center gap-1.5">
                        {o.orderNumber}
                        {o.latestNote && (
                          <span title={o.latestNote} className="text-text-tertiary hover:text-text-secondary transition-colors">
                            <NoteIcon className="h-3.5 w-3.5" />
                          </span>
                        )}
                      </span>
                    </td>
                    <td className="px-5 py-2.5 whitespace-nowrap">
                      <div className="min-w-0">
                        <span className="block truncate text-text-primary">{o.customerName || '—'}</span>
                        {o.customerEmail && (
                          <span className="block truncate text-[11px] text-text-tertiary">{o.customerEmail}</span>
                        )}
                      </div>
                    </td>
                    <td className="hidden px-5 py-2.5 truncate text-text-secondary md:table-cell max-w-[140px]">
                      {o.organizationName}
                    </td>
                    <td className="hidden px-5 py-2.5 lg:table-cell max-w-[160px]">
                      {o.store ? (
                        <div className="min-w-0">
                          <span className="block truncate text-text-secondary">
                            {o.store.name}
                          </span>
                          {o.store.shopDomain && (
                            <span className="block truncate text-[11px] text-text-tertiary">
                              {o.store.shopDomain}
                            </span>
                          )}
                        </div>
                      ) : (
                        <span className="text-text-tertiary italic">—</span>
                      )}
                    </td>
                    <td className="px-5 py-2.5 whitespace-nowrap">
                      <div className="flex flex-wrap items-center gap-1.5">
                        {/* Merchant-facing label; hover reveals the internal status */}
                        <Badge
                          variant={ORDER_STATUS_MERCHANT_VARIANT[o.status] ?? 'default'}
                          size="sm"
                          title={`Internal status: ${ORDER_STATUS_LABELS[o.status] ?? o.status.replace(/_/g, ' ')}`}
                        >
                          {getMerchantOrderStatusLabel(o.status)}
                        </Badge>
                        {/* The merchant remap hides some internal failures
                            (EXPORT_FAILED reads "Order Processing") — surface
                            them to ops with an explicit danger badge. */}
                        {isMerchantMaskedDanger(o.status) && (
                          <Badge variant="danger" size="sm">
                            {ORDER_STATUS_LABELS[o.status] ?? o.status.replace(/_/g, ' ')}
                          </Badge>
                        )}
                        {o.fulfillmentStatus === 'PARTIAL' && (
                          <Badge variant="info" size="sm">
                            PARTIALLY FULFILLED
                          </Badge>
                        )}
                        {o.fulfillmentStatus === 'FULFILLED' && (
                          <Badge variant="success" size="sm">
                            FULFILLED
                          </Badge>
                        )}
                        {o.fulfillmentMix === 'ALL_SELF' && (
                          <Badge variant="warning" size="sm">
                            SELF-FULFILL
                          </Badge>
                        )}
                        {o.fulfillmentMix === 'MIXED' && (
                          <Badge variant="info" size="sm">
                            MIXED FULFILLMENT
                          </Badge>
                        )}
                        {o.selfClaim?.status === 'UNCLAIMED' && (
                          <Badge variant="warning" size="sm">
                            UNCLAIMED
                          </Badge>
                        )}
                        {o.selfClaim?.status === 'CLAIMED' && (
                          <Badge variant="success" size="sm">
                            {o.selfClaim.providerName
                              ? `${o.selfClaim.providerName}${o.selfClaim.fulfillmentType ? ` · ${o.selfClaim.fulfillmentType}` : ''}`
                              : 'CLAIMED'}
                          </Badge>
                        )}
                        {o.hasProviderFailure && (
                          <Badge variant="danger" size="sm">
                            PROVIDER FAILED
                          </Badge>
                        )}
                      </div>
                    </td>
                    <td className="hidden px-5 py-2.5 sm:table-cell">
                      {/* Internal (admin, brand tint) + store (Shopify, neutral)
                          tags — first chip inline, the rest behind the "+N"
                          dropdown. Clicking a chip applies the tag filter. */}
                      <TagOverflowList
                        maxVisible={1}
                        groups={[
                          {
                            key: 'internal',
                            label: 'Internal',
                            tags: o.internalTags,
                            tone: 'brand',
                            onTagClick: (t) => setFilter('tag', t),
                          },
                          {
                            key: 'store',
                            label: 'Store',
                            tags: o.tags,
                            tone: 'neutral',
                            onTagClick: (t) => setFilter('tag', t),
                          },
                        ]}
                      />
                    </td>
                    <td className="hidden px-5 py-2.5 whitespace-nowrap md:table-cell">
                      {o.wholesaleChargeStatus && o.wholesaleChargeStatus !== 'NONE' ? (
                        <SettlementBadge status={o.wholesaleChargeStatus} />
                      ) : (
                        <span className="text-text-tertiary italic">—</span>
                      )}
                    </td>
                    <td className="hidden px-5 py-2.5 truncate text-text-secondary lg:table-cell max-w-[120px]">
                      {o.providerName ?? <span className="text-text-tertiary italic">—</span>}
                    </td>
                    <td className="hidden px-5 py-2.5 text-right whitespace-nowrap text-text-secondary sm:table-cell">
                      {o.itemCount}
                    </td>
                    <td className="px-5 py-2.5 text-right whitespace-nowrap font-medium text-text-primary">
                      ${o.total}
                    </td>
                    <td className="hidden px-5 py-2.5 whitespace-nowrap text-[12px] text-text-tertiary lg:table-cell">
                      {new Date(o.createdAt).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                      })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {pagination && pagination.totalPages > 1 && (
          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border px-5 py-3">
            <span className="text-xs text-text-tertiary">
              Page {pagination.page} of {pagination.totalPages}
            </span>
            <div className="flex gap-1.5">
              <button
                className="rounded-md border border-border bg-white px-3 py-1.5 text-xs font-medium text-text-primary transition-colors hover:bg-surface-secondary disabled:pointer-events-none disabled:opacity-50"
                disabled={page <= 1}
                onClick={() => setFilter('page', String(page - 1))}
              >
                Previous
              </button>
              <button
                className="rounded-md border border-border bg-white px-3 py-1.5 text-xs font-medium text-text-primary transition-colors hover:bg-surface-secondary disabled:pointer-events-none disabled:opacity-50"
                disabled={page >= pagination.totalPages}
                onClick={() => setFilter('page', String(page + 1))}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </Card>
      <SendToProviderModal
        open={bulkModalOpen}
        onClose={() => setBulkModalOpen(false)}
        title="Send to provider"
        confirmLabel="Send to provider"
        storeName={selectedStoreLabel}
        shopDomain={
          selectedEligible.length === 1
            ? selectedEligible[0]!.store?.shopDomain
            : null
        }
        selfLineCount={selectedSelfLineCount}
        orderCount={selectedEligible.length}
        skipCount={selectedSkipCount}
        mixedOrder={selectedEligible.some((o) => o.fulfillmentMix === 'MIXED')}
        fulfillmentType={bulkFulfillmentType}
        onFulfillmentTypeChange={setBulkFulfillmentType}
        productionType={bulkProductionType}
        onProductionTypeChange={setBulkProductionType}
        allowDetectedProduction
        productionNote={
          selectedEligible.some((o) => o.productionType === 'RUSH')
            ? 'Some selected orders are Rush from the rush-production tag. Detected per order keeps that. Regular or Rush applies to every order in this batch.'
            : 'Detected per order stays Regular unless the order tag is rush-production.'
        }
        checkoutSuggested={
          bulkMixedCheckout ? null : allPickupSuggested ? 'PICKUP' : 'SHIPPING'
        }
        checkoutLabel={
          allPickupSuggested
            ? 'Pickup in store'
            : bulkMixedCheckout
              ? null
              : 'Shipping'
        }
        mixedCheckoutNote={
          bulkMixedCheckout
            ? 'Selected orders have mixed Shopify checkout methods. The type below is sent to every order.'
            : null
        }
        pickupFallbackNote="Orders without a customer address use the provider warehouse, then the TK warehouse."
        onConfirm={() => bulkSendMutation.mutate()}
        loading={bulkSendMutation.isPending}
        error={bulkError}
      />
    </div>
  );
}

/**
 * Text filter input that syncs from the URL param and commits the typed
 * value after a short pause — avoids firing a list refetch per keystroke.
 */
function DebouncedFilterInput({
  label,
  placeholder,
  value,
  onCommit,
}: {
  label: string;
  placeholder?: string;
  value: string;
  onCommit: (value: string) => void;
}) {
  const [inputValue, setInputValue] = useState(value);
  const commitRef = useRef(onCommit);
  commitRef.current = onCommit;

  // External change (chip removed, quick filter, back/forward) → sync down.
  useEffect(() => {
    setInputValue(value);
  }, [value]);

  useEffect(() => {
    if (inputValue === value) return;
    const timer = setTimeout(() => commitRef.current(inputValue), 400);
    return () => clearTimeout(timer);
  }, [inputValue, value]);

  return (
    <Input
      label={label}
      placeholder={placeholder}
      value={inputValue}
      onChange={(e) => setInputValue(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') commitRef.current(inputValue);
      }}
    />
  );
}

function FilterChip({ label, onRemove }: { label: string; onRemove: () => void }) {
  return (
    <span className="inline-flex items-center gap-1 rounded-full border border-border bg-surface px-2 py-0.5 text-[11px] font-medium text-text-secondary">
      {label}
      <button
        onClick={(e) => {
          e.stopPropagation();
          onRemove();
        }}
        className="rounded-full p-0.5 hover:bg-surface-secondary transition-colors"
        aria-label={`Remove filter: ${label}`}
      >
        <XIcon className="h-3 w-3" />
      </button>
    </span>
  );
}
